import Functions from '@client/style/Functions';
import { Breakpoints } from '@client/style/Variables';
import React, { Fragment, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ArtistSearchResult, GroupedSearchResults, isArtistResult } from './search-utils';
import { TextStyle } from '@client/style/TextStyles';
import Image from '@client/modules/Image';
import { FavouritePanelCompact } from '@client/modules/Favourites/FavouritePanelCompact';
import Link from '@client/core/PageSwitch/Link';

const ResultsGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;

	${Functions.breakpoint(Breakpoints.mobile)} {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-auto-rows: max-content;
		gap: 10px;
	}

	${Functions.breakpoint(Breakpoints.laptop)} {
		gap: 20px;
	}

	${Functions.breakpoint(Breakpoints.ultra)} {
		grid-template-columns: repeat(4, 1fr);
	}
`;

const SectionTitle = styled.h3`
	display: none;
	margin: 0;
	padding: 0;
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${TextStyle.size('p4')}

	${Functions.breakpoint(Breakpoints.mobile)} {
		display: block;
		grid-column: 1 / -1;
		margin-top: 80px;
	}
`;

const ResultItem = styled(Link)<{ $large?: boolean; $hasImage?: boolean }>`
	${props =>
		props.$hasImage
			? css`
					display: grid;
					grid-template-columns: max-content 1fr max-content;
					grid-template-rows: max-content max-content;
					grid-template-areas: 'image title favourite' 'image detail favourite';
					gap: 0 14px;
					padding: 10px;
				`
			: css`
					display: flex;
					flex-direction: column;
					padding: 15px 20px;
					gap: 5px;
				`}
	background-color: #ffffff;
	min-width: 0;

	${Functions.breakpoint(Breakpoints.mobile)} {
		background-color: ${props => (props.$large ? '#FFFFFF' : '#F8F0EB')};
		${props => (props.$large ? 'grid-column-end: span 2;' : '')}
		${props => (props.$hasImage ? `gap: 0 32px;` : `padding: 20px;`)}
	}

	${Functions.breakpoint(Breakpoints.laptop)} {
		${props => (props.$hasImage ? `gap: 0 64px;` : `padding: 40px;`)}
	}
`;

const ResultTitle = styled.h4<{ $large?: boolean }>`
	grid-area: title;
	align-self: end;
	width: 100%;
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${props => TextStyle.size('p4', props.$large ? 'p2' : 'p3', 'laptop')}
	color: #262C2E;
	margin: 0;
	padding: 0;
	hyphens: auto;
	overflow-wrap: anywhere;
`;

const ResultDetail = styled.h4`
	grid-area: detail;
	align-self: start;
	width: 100%;
	${TextStyle.family('title')}
	${TextStyle.size('small', 'large', 'laptop')}
	color: #262C2E;
	opacity: 0.7;
	margin: 0;
	padding: 0;
`;

const ResultImage = styled(Image)`
	grid-area: image;
	height: 5.5em;
	width: 5.5em;

	${Functions.breakpoint(Breakpoints.mobile)} {
		height: 8em;
		width: 8em;
	}

	${Functions.breakpoint(Breakpoints.tablet)} {
		height: 8.75em;
		width: 8.75em;
	}

	${Functions.breakpoint(Breakpoints.laptop)} {
		height: 9.875em;
		width: 9.875em;
	}
`;

const StyledFavouriteButton = styled(FavouritePanelCompact)`
	grid-area: favourite;
`;

export const ArtistDetail = ({ data }: { data: ArtistSearchResult }) => {
	const date = useMemo(() => {
		if (!data.start_time) return;
		const date = new Date(data.start_time);
		let str = date.toLocaleDateString('da-DK', {
			weekday: 'long',
		});
		str = str[0].toUpperCase() + str.slice(1);
		if (data.show_time) {
			str +=
				' kl. ' +
				date.toLocaleTimeString('da-DK', {
					hour: 'numeric',
					minute: 'numeric',
				});
		}
		return str;
	}, [data.start_time, data.show_time]);

	const detail = useMemo(() => {
		if (!date && !data.location) return;
		return [date, data.location].filter(Boolean).join(', ');
	}, [date, data.location]);

	if (!date && !data.location) return;

	return <ResultDetail>{detail}</ResultDetail>;
};

export default function SearchResults({ data }: { data: GroupedSearchResults }) {
	return (
		<ResultsGrid>
			{data.map(group => (
				<Fragment key={group.label}>
					<SectionTitle>{group.label}</SectionTitle>
					{group.items.map(item => (
						<ResultItem
							key={item.id}
							$large={isArtistResult(item)}
							$hasImage={isArtistResult(item) && !!item.images[0]?.url}
							data={{
								title: item.title,
								link: item.slug,
							}}
						>
							{isArtistResult(item) && item.images[0]?.url && <ResultImage src={item.images[0].url} alt={item.title} />}
							<ResultTitle $large={isArtistResult(item)}>{item.title}</ResultTitle>
							{isArtistResult(item) && <ArtistDetail data={item} />}
							{isArtistResult(item) && <StyledFavouriteButton artistId={item.id} />}
							{/* {isContentResult(item) && item.preview_text ? <ResultDetail>{item.preview_text}</ResultDetail> : null} */}
						</ResultItem>
					))}
				</Fragment>
			))}
		</ResultsGrid>
	);
}
